import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Checkbox, Dropdown, Groupper, Header, Icon, Input, Message } from 'react-frontier';
import { useBreadcrumbs } from '../Hooks';
import { MetadataType, PromoterFormMetadata, SetLoading, UserClass } from '../SiteClasses';
import Validator from '../Validator';
import Toast from '../Toast';
import API from '../API';

interface ProspectRegisterProps{
	mode: 'public' | 'admin',
}

interface QuestionFieldProps{
	question: PromoterFormMetadata,
	value: string,
	options?: { value_id: number, value: string }[],
	onChange: (metadata_id: number, value: string)=>void,
}

var QuestionField = (props: QuestionFieldProps)=>{
	if(props.question.type_id===MetadataType.CHECKBOX){
		return <Checkbox
			checked={props.value==='1'}
			label={props.question.metadata_comment || props.question.metadata_name}
			onChange={v=>props.onChange(props.question.metadata_id, v ? '1' : '0')}
		/>
	}else if(props.question.type_id===MetadataType.DROPDOWN || props.question.type_id===MetadataType.MULTIPLE){
		return <Dropdown
			label={props.question.metadata_name}
			comment={props.question.metadata_comment}
			items={!props.options ? [] : props.options.map(a=>({ text: a.value, value: a.value_id.toString() }))}
			value={props.value || null}
			onValueChange={v=>props.onChange(props.question.metadata_id, v || '')}
		/>
	}else{
		return <Input
			fluid
			label={props.question.metadata_name}
			comment={props.question.metadata_comment}
			textarea={props.question.type_id===MetadataType.TEXTAREA}
			textareaRows={3}
			value={props.value ?? ''}
			onChange={v=>props.onChange(props.question.metadata_id, v)}
		/>
	}
}

var ProspectRegister = (props: ProspectRegisterProps)=>{
	var navigate = useNavigate();
	var { setTitle } = useBreadcrumbs() || ({} as any);
	var [form, setForm] = useState<PromoterFormMetadata[]>(null);
	var [loadError, setLoadError] = useState<string>(null);
	var [name, setName] = useState<string>('');
	var [answers, setAnswers] = useState<{ [metadata_id: number]: string }>({});
	var [options, setOptions] = useState<{ [metadata_id: number]: { value_id: number, value: string }[] }>({});
	var [prompts, setPrompts] = useState<string[]>(null);
	var [done, setDone] = useState<boolean>(false);
	var [users, setUsers] = useState<UserClass[]>(null);
	var [userAssigned, setUserAssigned] = useState<number>(null);

	useEffect(()=>{
		if(props.mode==='admin' && setTitle) setTitle('Registrar prospecto');
		else if(props.mode==='public') document.title = 'Registro de prospecto - Arema CRM';

		var templateFn = props.mode==='admin' ? API.getProspectTemplate : API.getPublicProspectTemplate;
		var valuesFn = props.mode==='admin' ? API.getMetadataValues : API.getPublicMetadataValues;

		if(props.mode==='admin'){
			API.getUsers(200, 0).then(res=>{
				if(!res.error) setUsers(res.data || []);
			}).catch(()=>{});
		}

		templateFn().then(res=>{
			if(res.error) return setLoadError(res.message);
			var data: PromoterFormMetadata[] = res.data;
			setForm(data);

			var dropdowns = data.filter(a=>a.type_id===MetadataType.DROPDOWN || a.type_id===MetadataType.MULTIPLE);
			if(dropdowns.length>0){
				Promise.all(dropdowns.map(q=>
					valuesFn(q.metadata_id).then(r=>({ metadata_id: q.metadata_id, values: r.error ? [] : r.data }))
				)).then(results=>{
					var opts: typeof options = {};
					for(var r of results) opts[r.metadata_id] = r.values;
					setOptions(opts);
				});
			}
		}).catch(()=>{
			setLoadError('Hubo un error inesperado cargando el formulario. (LCL-PRREG-1)');
		});
	}, [props.mode]);

	var onAnswerChange = (metadata_id: number, value: string)=>{
		setAnswers(prev=>({ ...prev, [metadata_id]: value }));
	}

	var buildAnswers = ()=>{
		return form.map(q=>{
			var raw = answers[q.metadata_id] ?? '';
			if(q.type_id===MetadataType.DROPDOWN || q.type_id===MetadataType.MULTIPLE){
				return raw ? { metadata_id: q.metadata_id, value_id: parseInt(raw) } : null;
			}
			return (raw!=='' && raw!=null) ? { metadata_id: q.metadata_id, value: raw } : null;
		}).filter(a=>!!a);
	}

	var submit = (setLoading: SetLoading)=>{
		var { valid, prompts } = Validator({ name }, {
			name: [{ rule: 'length', params: [2, 128], label: 'Nombre' }]
		});
		var errors = [...(prompts || [])];
		if(props.mode==='admin' && userAssigned==null) errors.push('Selecciona un usuario a asignar');
		if(props.mode!=='admin'){
			var unanswered = form.filter(q=>q.type_id!==MetadataType.CHECKBOX && !(answers[q.metadata_id] ?? '').trim());
			for(var q of unanswered) errors.push(`Falta responder: ${q.metadata_name}`);
		}
		setPrompts(errors);
		if(!valid || errors.length>0) return;
		setLoading(true);

		var registerFn = props.mode==='admin'
			? ()=>API.createProspect(name.trim(), buildAnswers(), userAssigned)
			: ()=>API.registerProspect(name.trim(), buildAnswers());
		registerFn().then(res=>{
			if(res.error) return setPrompts([res.message]);
			if(props.mode==='admin'){
				Toast.success('Prospecto registrado exitosamente');
				navigate('/');
			}else{
				setDone(true);
			}
		}).catch(()=>{
			setPrompts(['Hubo un error inesperado registrando el prospecto. (LCL-PRREGSUB-1)']);
		}).finally(()=>{
			setLoading(false);
		});
	}

	var renderBody = ()=>{
		if(loadError) return <div style={{ padding: 24 }}><Message type='error' header='Error'>{loadError}</Message></div>;
		if(!form) return <div style={{ padding: 40 }}><Header loading text='Cargando formulario' /></div>;
		if(done) return <div style={{ padding: '48px 24px', textAlign: 'center' }}>
			<div style={{ width: 140, height: 140, borderRadius: '50%', backgroundColor: 'var(--color-green, #21ba45)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 28px' }}>
				<Icon name='check' style={{ color: '#fff', fontSize: 90, margin: 0, lineHeight: 1 }} />
			</div>
			<div style={{ fontSize: 22, fontWeight: 700, marginBottom: 10 }}>¡Registro exitoso!</div>
			<div style={{ fontSize: 15, color: 'var(--color-gray-500, #888)', lineHeight: 1.5, maxWidth: 360, margin: '0 auto' }}>
				Tu información fue recibida correctamente. Nuestro equipo se pondrá en contacto contigo pronto.
			</div>
		</div>;

		return <>
			<div>
				<Input fluid label='Empresa organizadora' placeholder='Nombre de la empresa' value={name} onChange={setName} />
				{props.mode==='admin' && (
					<Dropdown
						label='Asignar a'
						search
						placeholder={users ? 'Selecciona un usuario' : 'Cargando...'}
						value={userAssigned!=null ? String(userAssigned) : null}
						items={(users || []).map(u=>({ text: `${u.first_name} ${u.last_name}`, value: String(u.user_id) }))}
						onValueChange={v=>setUserAssigned(v!=null && v!=='' ? parseInt(v) : null)}
						style={{ marginTop: 12 }}
					/>
				)}
			</div>
			{form.length>0 && <Groupper.Divider text='Formulario'/>}
			<div>
				{form.map(q=>(
					<div key={`PRQ-${q.metadata_id}`} style={{ marginBottom: 16 }}>
						<QuestionField question={q} value={answers[q.metadata_id] ?? ''} options={options[q.metadata_id]} onChange={onAnswerChange} />
					</div>
				))}
			</div>
		</>;
	}

	var card = <Groupper 
		title={props.mode==='public' ? 'Registro' : 'Registro de prospecto'}
		titleCentered
		actions={(done || loadError || !form) ? undefined : <Button text='Registrar' color='black' onClick={submit} />}
		style={{ maxWidth: 500, margin: 'auto', marginTop: 20 }}>
		{renderBody()}
	</Groupper>;

	if(props.mode==='public'){
		return <div style={{ minHeight: '100vh', backgroundColor: '#f5f5f5', padding: '40px 16px' }}>
			{card}
		</div>;
	}
	return <div>{card}</div>;
}

export default ProspectRegister;
